// @flow
import moment from 'moment';
import _ from 'lodash';
import validate from 'validate.js';
import { hasPermission } from 'utils/user-utils';
import { DEFAULT_DATE_FORMAT } from 'utils/time-utils';

export const dateRange = function(value: any, options: any, key: any, object: any) {
  const { start: startDateKey, end: endDateKey, format = DEFAULT_DATE_FORMAT } = options;
  let startDate = _.get(object, startDateKey);
  let endDate = _.get(object, endDateKey);
  if (_.isString(startDate) || _.isString(endDate)) {
    startDate = moment(startDate, format);
    endDate = moment(endDate, format);
  } else if (!startDate || !endDate) {
    return;
  }

  if (startDate.isAfter(endDate)) {
    if (key === startDateKey) {
      return 'can\'t be greater than End date';
    }
    return 'can\'t be less than Start date';
  }
};

// Only validate presence if the condition is matched
// conditions is an array of 'prop' and desired 'value'
export const conditionalRequired = function(value: any, conditions: any, key: any, object: any) {
  const shouldValidate = conditions.reduce((result, condition) => {
    // In case field prop in object is nested in a big object, it will not works
    // So we have to take the absolute path rather than ralative.
    // {
    //   online.cta.android: {
    //     type: ...,
    //     launch: ...,
    //   }
    // }

    // Remove last item
    const keyRequired = [..._.dropRight(key.split('.')), condition.prop];
    const objectValue = _.get(object, keyRequired);
    return result && objectValue === condition.value;
  }, true);

  if (shouldValidate && validate.isEmpty(value)) {
    return "can't be blank";
  }
};

export const conditionalConstraints = function(value: any, conditionObject: any, key: any, object: any) {
  const { conditions, constraints } = conditionObject;
  const shouldValidate = conditions.reduce((result, condition) => {
    const objectValue = _.get(object, condition.prop);
    if (_.get(condition, 'notEqual')) {
      return result && objectValue !== condition.value;
    }
    if (_.get(condition, 'in')) {
      return result && _.indexOf(condition.value, objectValue) > -1;
    }
    return result && objectValue === condition.value;
  }, true);

  if (shouldValidate) {
    let error = _.get(validate(object, { [key]: constraints }), key);
    if (_.isArray(error)) {
      error = error.join(', ');
    }
    if (error) {
      return `^${error}`;
    }
  }
};

export const optional = function(value: any, constraints: any, key: any, object: any) {
  if (validate.isEmpty(value)) {
    return undefined;
  }

  let error = _.get(validate(object, { [key]: constraints }), key);
  if (_.isArray(error)) {
    error = error.join(', ');
  }
  if (error) {
    return `^${error}`;
  }
};

export const hasCover = function(value: any) {
  if (!_.find(value, ['type', 'background'])) {
    return 'must have a cover';
  }
};

export const hasImage = function(value: any) {
  if (_.findIndex(value, ['type', 'fullImage']) === -1) {
    return 'must have at least one image';
  }
};

export const hasHero = function(value: any) {
  if (_.findIndex(value, ['type', 'hero']) === -1) {
    return 'must have a hero';
  }
};

export const notPresent = (value: any) => {
  return value !== undefined ? 'should not be present' : null;
};

export const errorTemplates = {
  tooShort: (attributeName: string, value: number) => `^The length of ${attributeName} must not be less than ${value}`,
  tooLong: (attributeName: string, value: number) => `^The length of ${attributeName} must not be more than ${value}`,
};

export const constraintTemplates = {
  lengthRange: (attributeName: string, minimum: number, maximum: number) => ({
    length: {
      minimum,
      tooShort: errorTemplates.tooShort(attributeName, minimum),
      maximum,
      tooLong: errorTemplates.tooLong(attributeName, maximum),
    },
  }),
};

// Can be used for very specific cases where you need single use custom validation function
export const validateByFunc = function(val: any, options: any, __: any, object: any) {
  const { validationFunc } = options;
  return validationFunc(object);
};

export const greaterThanModel = function(__: any, options: any, ___: any, object: any) {
  const { lesserValue: lesserValueKey, currentValue: currentValueKey } = options;
  const lesserValue = _.get(object, lesserValueKey);
  const currentValue = _.get(object, currentValueKey);
  if (currentValue > 0 && currentValue < lesserValue) {
    return `can't be lesser than ${lesserValueKey}`;
  }
};

// hasRules checks for the object for mandatory conditions in the rules.
// Pass a constraint with key to be checked and the value for it to be checking the mandatory rules
// hasMultipleRules as a boolean to check if it requires multiple rules on the object
// rules array with the variable names for them to be mandatorily present in the object
export const hasRules = function(__: any, options: any, key: any, object: any) {
  const error = [];
  _.map(options, (option) => {
    const { key, value } = option;
    const currentValue = _.get(object, key);
    if (currentValue === value) {
      const isMultiple = _.get(option, 'hasMultipleRules');
      if (!object.rule) {
        error.push('should have conditions');
      }
      if (isMultiple && object.rule) {
        if (!object.rule.conditions || (object.rule.conditions.length < 2)) {
          error.push('should have more than one condition');
        }
        if (object.rule.conditions) {
          _.map(option.rules, (rule) => {
            const found = checkRule(object.rule, rule);
            if (!found) {
              error.push(`for ${value} requires rule for variable ${rule} with proper value`);
            }
          });
        }
      }
      if (!isMultiple && object.rule) {
        if (object.rule.conditions) {
          _.map(option.rules, (rule) => {
            const found = regexCheck(object.rule, rule);
            if (!found) {
              error.push(`for ${value} requires rule for variable ${rule} with proper value`);
            }
          });
        } else {
          _.map(option.rules, (rule) => {
            let found = false;
            if (object.rule.lhs.includes(rule) && object.rule.rhs !== '') {
              found = true;
            }
            if (!found) {
              error.push(`for ${value} requires rule for variable ${rule} with proper value`);
            }
          });
        }
      }
    }
  });
  if (error.length > 0) {
    return error.join(', ');
  }
};

// checkRule checks for the specific rule in the rule object recursively and returns a boolean
function checkRule(object: any, rule: string) {
  return _.some(object.conditions, (condition: Object) => {
    if (_.get(condition, 'lhs') === rule && _.get(condition, 'rhs') !== '') {
      return true;
    }
    if (condition.conditions) {
      return checkRule(condition, rule);
    }
    return false;
  });
}

// regexCheck function does regex check for the specific rule in the rule object recursively and returns a boolean
function regexCheck(object: any, rule: string) {
  return _.some(object.conditions, (condition: Object) => {
    if (condition.conditions) {
      return regexCheck(condition, rule);
    }
    if (_.get(condition, 'lhs').includes(rule) && _.get(condition, 'rhs') !== '') {
      return true;
    }
    return false;
  });
}

validate.validators.notPresent = notPresent;
validate.validators.dateRange = dateRange;
validate.validators.conditionalRequired = conditionalRequired;
validate.validators.conditionalConstraints = conditionalConstraints;

// To allow custom validations like nestedValidate
export function customValidate(model: Object, constraints: Object, userPermissions: any, permissionKey: any, permissionType: any, aclFields: any) {
  const nestedConstraints = _.cloneDeep(constraints);
  if (userPermissions) {
    _.forEach(aclFields, (field) => !hasPermission(userPermissions, permissionKey, field, permissionType, true) && _.unset(nestedConstraints, field));
  }
  for (const key in constraints) {
    if (_.get(constraints, [key, 'nestedValidate'])) {
      // If nestedValidate is true, validate each element of the collection individually
      _.forEach(
        // By using assign, this should also work with arrays
        _.keys(_.assign({}, _.get(model, key))),
        (childKey) =>
          _.set(nestedConstraints, [`${key}.${childKey}`], _.omit(constraints[key], ['nestedValidate'])));
      // Unset the parent key from constraints as child keys are already added
      _.unset(nestedConstraints, key);
    }
  }
  return validate(model, nestedConstraints);
}
